<!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
<!-- SPEC 33 / P37-8 — Painel de Administração (hub admin-only) -->
<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useTracking } from 'dashboard/composables/useTracking';
import AgencyProfileAPI from 'dashboard/api/agencyProfile';
import DataQualityGovernanceAPI from 'dashboard/api/dataQualityGovernance';
import FinanceiroAPI from 'dashboard/api/financeiro';
import PartnersAPI from 'dashboard/api/partners';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisErrorState from 'dashboard/components-next/LaisErrorState.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPermissionState from 'dashboard/components-next/LaisPermissionState.vue';
import LaisRunStatusPanel from 'dashboard/components-next/LaisRunStatusPanel.vue';

const store = useStore();
const account = useAccount();
const router = useRouter();
const route = useRoute();
const accountId = useMapGetter('getCurrentAccountId');
const currentRole = useMapGetter('getCurrentRole');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const { track } = useTracking();

const ADMIN_TAB_KEYS = Object.freeze({
  GOVERNANCA: 'governanca',
  OPERACAO: 'operacao',
  PARCEIROS: 'parceiros',
  CONFIGURACAO: 'configuracao',
});

const ADMIN_TABS = Object.freeze([
  {
    key: ADMIN_TAB_KEYS.GOVERNANCA,
    label: 'Governança',
    helper: 'Qualidade, auditoria e perfil institucional.',
  },
  {
    key: ADMIN_TAB_KEYS.OPERACAO,
    label: 'Operação',
    helper: 'SLA, tickets, metas, desempenho e comissões.',
  },
  {
    key: ADMIN_TAB_KEYS.PARCEIROS,
    label: 'Parceiros',
    helper: 'Prestadores, grants e ordens de serviço.',
  },
  {
    key: ADMIN_TAB_KEYS.CONFIGURACAO,
    label: 'Configuração',
    helper: 'Owners nativos de agentes, inboxes, billing e ajustes.',
  },
]);
const DEFAULT_ADMIN_TAB = ADMIN_TAB_KEYS.GOVERNANCA;
const ADMIN_TAB_KEY_SET = new Set(ADMIN_TABS.map(tab => tab.key));

const isAdmin = computed(() => currentRole.value === 'administrator');
const agentsLoading = ref(false);
const agentsError = ref(false);
const financeiroLoading = ref(false);
const financeiroError = ref(false);
const partnersLoading = ref(false);
const partnersError = ref(false);
const planBadge = ref(null);
const partnersBadge = ref(null);
const qualityLoading = ref(false);
const qualityError = ref(false);
const qualityBadge = ref(null);
const qualityReviewCount = ref(0);
const agencyLoading = ref(false);
const agencyError = ref(false);
const agencyBadge = ref(null);
const agencyBlockedCount = ref(0);
const hasLoadedAdminHub = ref(false);
const adminSummaryStartedAt = ref(null);
const adminSummaryFinishedAt = ref(null);

const PLAN_STATUS_LABELS = {
  active: 'Ativo',
  ativo: 'Ativo',
  trial: 'Trial',
  trialing: 'Trial',
  past_due: 'Em atraso',
  overdue: 'Em atraso',
  atrasado: 'Em atraso',
  canceled: 'Cancelado',
  cancelado: 'Cancelado',
  inactive: 'Inativo',
  inativo: 'Inativo',
};

const agents = computed(() => store.getters['agents/getAgents'] || []);
const membersCount = computed(() => agents.value.length);
const pendingInvitesCount = computed(
  () => agents.value.filter(agent => agent.confirmed === false).length
);

const agentManagementEnabled = computed(() =>
  isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.AGENT_MANAGEMENT
  )
);

const humanizeStatus = value =>
  String(value)
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());

const planStatusLabel = plan => {
  if (!plan) return null;
  if (plan.cancelamento_agendado) return 'Cancelamento agendado';

  const rawStatus = plan.status || plan.situacao || plan.state;
  if (rawStatus) {
    const status = String(rawStatus).toLowerCase();
    return PLAN_STATUS_LABELS[status] || humanizeStatus(status);
  }

  return plan.nome && plan.nome !== '-' ? 'Ativo' : null;
};

const firstQueryValue = value => (Array.isArray(value) ? value[0] : value);

const normalizeAdminTab = value => {
  const tab = String(firstQueryValue(value) || '').toLowerCase();
  return ADMIN_TAB_KEY_SET.has(tab) ? tab : DEFAULT_ADMIN_TAB;
};

const activeTabKey = ref(normalizeAdminTab(route.query.tab));
const activeTab = computed(
  () => ADMIN_TABS.find(tab => tab.key === activeTabKey.value) || ADMIN_TABS[0]
);

const replaceRouteTab = tabKey =>
  router.replace({
    name: 'administracao_index',
    params: route.params,
    query: { ...route.query, tab: tabKey },
  });

const selectAdminTab = tabKey => {
  const normalizedTab = normalizeAdminTab(tabKey);
  const currentRouteTab = firstQueryValue(route.query.tab);

  activeTabKey.value = normalizedTab;

  if (currentRouteTab === normalizedTab) return;

  track('admin_hub_tab_clicked', { tab: normalizedTab });
  router.push({
    name: 'administracao_index',
    params: route.params,
    query: { ...route.query, tab: normalizedTab },
  });
};

const agentCards = computed(() =>
  agentManagementEnabled.value
    ? [
        {
          tab: ADMIN_TAB_KEYS.CONFIGURACAO,
          slug: 'usuarios',
          label: 'Usuários & Papéis',
          helper: 'Gerencie corretores, papéis e permissões de acesso.',
          operation: 'Abrir lista nativa de agentes.',
          owner: 'Settings nativo',
          dependency:
            'Sem duplicar: cadastro e edição de agentes continuam no owner nativo.',
          badge: agentsError.value ? null : membersCount.value,
          badgeLoading: agentsLoading.value,
          to: account.accountScopedRoute('agent_list'),
          subTo: account.accountScopedRoute('custom_roles_list'),
          subLabel: 'Papéis e permissões',
        },
        {
          tab: ADMIN_TAB_KEYS.CONFIGURACAO,
          slug: 'convites',
          label: 'Convites pendentes',
          helper: 'Convites enviados aguardando confirmação de e-mail.',
          operation: 'Abrir agentes filtrados por convites pendentes.',
          owner: 'Settings nativo',
          dependency:
            'Sem duplicar: aceite, reenvio e remoção ficam no console de agentes.',
          badge: agentsError.value ? null : pendingInvitesCount.value,
          badgeLoading: agentsLoading.value,
          to: account.accountScopedRoute('agent_list', undefined, {
            filter: 'pending',
          }),
        },
      ]
    : []
);

const cards = computed(() => [
  {
    tab: ADMIN_TAB_KEYS.GOVERNANCA,
    slug: 'imobiliaria',
    label: 'Imobiliária',
    helper: 'Filiais, CRECI/licenças, territórios, vitrine e contexto ativo.',
    operation: 'Abrir governança do perfil imobiliário.',
    owner: 'Área da conta',
    badge: agencyError.value ? null : agencyBadge.value,
    badgeLoading: agencyLoading.value,
    to: account.accountScopedRoute('imobiliaria_perfil_index'),
  },
  {
    tab: ADMIN_TAB_KEYS.GOVERNANCA,
    slug: 'qualidade_fraude',
    label: 'Qualidade & fraude',
    helper:
      'Duplicidades, score, sinais de fraude, alertas e decisoes humanas reversiveis.',
    operation: 'Abrir console real de qualidade e fraude.',
    owner: 'Administração',
    badge: qualityError.value ? null : qualityBadge.value,
    badgeLoading: qualityLoading.value,
    to: account.accountScopedRoute('administracao_qualidade_fraude_index'),
  },
  {
    tab: ADMIN_TAB_KEYS.GOVERNANCA,
    slug: 'auditoria',
    label: 'Auditoria',
    helper: 'Trilha de ações create/update/delete da conta.',
    operation: 'Abrir histórico operacional com visão de auditoria.',
    owner: 'Histórico operacional',
    to: account.accountScopedRoute('operational_history_index', undefined, {
      view: 'auditoria',
    }),
  },
  {
    tab: ADMIN_TAB_KEYS.OPERACAO,
    slug: 'ocorrencias',
    label: 'Ocorrências administrativas & SLA',
    helper: 'Fila de triagem, prioridade, responsáveis e vencimento de SLA.',
    operation: 'Abrir tickets no recorte de triagem administrativa.',
    owner: 'Portal de tickets',
    to: account.accountScopedRoute('support_tickets_portal_index', undefined, {
      view: 'triage',
    }),
  },
  {
    tab: ADMIN_TAB_KEYS.OPERACAO,
    slug: 'tickets_portal',
    label: 'Portal de Tickets da imobiliária',
    helper: 'Tickets da conta, criados por mim e por colaboradores.',
    operation: 'Abrir portal autenticado de tickets da conta.',
    owner: 'Portal de tickets',
    to: account.accountScopedRoute('support_tickets_portal_index'),
  },
  {
    tab: ADMIN_TAB_KEYS.OPERACAO,
    slug: 'desempenho_equipe',
    label: 'Desempenho da equipe',
    helper: 'Produtividade, atendimento e ranking por corretor/equipe.',
    operation: 'Abrir relatório operacional de desempenho.',
    owner: 'Relatórios',
    to: account.accountScopedRoute('desempenho_equipe_index'),
  },
  {
    tab: ADMIN_TAB_KEYS.OPERACAO,
    slug: 'metas',
    label: 'Metas',
    helper: 'Metas por agente ou equipe com ritmo e alertas de gestor.',
    operation: 'Abrir acompanhamento de metas.',
    owner: 'Relatórios',
    to: account.accountScopedRoute('metas_index'),
  },
  {
    tab: ADMIN_TAB_KEYS.OPERACAO,
    slug: 'comissoes',
    label: 'Comissões',
    helper: 'Atribuição, fechamento e metas de comissão da equipe.',
    operation: 'Abrir aba de comissões no Financeiro.',
    owner: 'Financeiro',
    to: account.accountScopedRoute('financeiro_comissoes_index'),
  },
  {
    tab: ADMIN_TAB_KEYS.PARCEIROS,
    slug: 'parceiros_grants_ordens',
    label: 'Parceiros, grants e ordens',
    helper:
      'Prestadores, permissões com expiração e ordens vinculadas a ticket/imóvel.',
    operation: 'Abrir console real de parceiros, grants e ordens.',
    owner: 'Administração',
    badge: partnersError.value ? null : partnersBadge.value,
    badgeLoading: partnersLoading.value,
    to: account.accountScopedRoute('administracao_parceiros_index'),
  },
  ...agentCards.value,
  {
    tab: ADMIN_TAB_KEYS.CONFIGURACAO,
    slug: 'inboxes_nativos',
    label: 'Canais e inboxes',
    helper: 'Caixas de entrada, canais e roteamento de atendimento.',
    operation: 'Abrir lista nativa de inboxes.',
    owner: 'Settings nativo',
    dependency:
      'Sem duplicar: criação, canais e agentes por inbox ficam no Settings.',
    to: account.accountScopedRoute('settings_inbox_list'),
  },
  {
    tab: ADMIN_TAB_KEYS.CONFIGURACAO,
    slug: 'cobranca',
    label: 'Cobrança',
    helper: 'Plano, faturas e cobrança de atendimento.',
    operation: 'Abrir visão financeira da conta.',
    owner: 'Financeiro',
    dependency:
      'Billing nativo permanece no Settings quando o ambiente expõe cobrança cloud.',
    badge: financeiroError.value ? null : planBadge.value,
    badgeLoading: financeiroLoading.value,
    to: account.accountScopedRoute('financeiro_index'),
  },
  {
    tab: ADMIN_TAB_KEYS.CONFIGURACAO,
    slug: 'configuracoes',
    label: 'Configurações',
    helper: 'Ajustes gerais da conta e da Lais.',
    operation: 'Abrir configurações LAIS da conta.',
    owner: 'Minha LAIS',
    dependency:
      'Sem replicar owners nativos: esta entrada só concentra ajustes LAIS.',
    to: account.accountScopedRoute('minha_lais_index'),
  },
]);

const cardsByTab = computed(() =>
  ADMIN_TABS.reduce((result, tab) => {
    result[tab.key] = cards.value.filter(card => card.tab === tab.key);
    return result;
  }, {})
);
const activeCards = computed(() => cardsByTab.value[activeTabKey.value] || []);
const tabCardCount = tabKey => cardsByTab.value[tabKey]?.length || 0;

const isLoadingAdminHub = computed(
  () =>
    agentsLoading.value ||
    financeiroLoading.value ||
    partnersLoading.value ||
    qualityLoading.value ||
    agencyLoading.value
);
const criticalSummaryLabels = computed(() =>
  [
    agentManagementEnabled.value ? 'Usuários' : null,
    'Imobiliária',
    'Cobrança',
    'Parceiros',
    'Qualidade',
  ].filter(Boolean)
);
const failedSummaryLabels = computed(() =>
  [
    agentManagementEnabled.value && agentsError.value ? 'Usuários' : null,
    agencyError.value ? 'Imobiliária' : null,
    financeiroError.value ? 'Cobrança' : null,
    partnersError.value ? 'Parceiros' : null,
    qualityError.value ? 'Qualidade' : null,
  ].filter(Boolean)
);
const hasPartialAdminSummary = computed(
  () =>
    failedSummaryLabels.value.length > 0 &&
    failedSummaryLabels.value.length < criticalSummaryLabels.value.length
);
const hasTotalAdminSummaryError = computed(
  () =>
    hasLoadedAdminHub.value &&
    !isLoadingAdminHub.value &&
    failedSummaryLabels.value.length === criticalSummaryLabels.value.length
);
const adminRunStatus = computed(() => {
  if (isLoadingAdminHub.value) return 'running';
  if (hasTotalAdminSummaryError.value) return 'failed';
  if (hasPartialAdminSummary.value) return 'stale';
  if (hasLoadedAdminHub.value) return 'completed';
  return 'queued';
});
const adminRunNextSteps = computed(() => {
  if (hasTotalAdminSummaryError.value) {
    return [
      'Tente novamente antes de tomar decisões administrativas.',
      'Verifique APIs de qualidade, parceiros, financeiro, imobiliária e agentes.',
    ];
  }

  if (hasPartialAdminSummary.value) {
    return [
      `Recarregue os resumos com falha: ${failedSummaryLabels.value.join(', ')}.`,
      'Os consoles seguem navegáveis, mas os badges podem estar defasados.',
    ];
  }

  return [
    `Aba ativa: ${activeTab.value.label}.`,
    'Use cada entrada como navegação para o owner operacional indicado.',
  ];
});
const adminRunStatusPanel = computed(() => ({
  status: adminRunStatus.value,
  startedAt: adminSummaryStartedAt.value,
  finishedAt: adminSummaryFinishedAt.value,
  worker: 'administracao_summary',
  lock: {
    active_tab: activeTabKey.value,
    visible_cards: activeCards.value.length,
  },
  error: hasTotalAdminSummaryError.value
    ? failedSummaryLabels.value.join(', ')
    : '',
  stale: hasPartialAdminSummary.value,
  progress: isLoadingAdminHub.value ? 45 : null,
  nextSteps: adminRunNextSteps.value,
}));

const goToCard = card => {
  if (card.disabled) return;
  track('admin_hub_card_clicked', { card: card.slug });
  router.push(card.to);
};

const loadAgents = async () => {
  agentsLoading.value = true;
  agentsError.value = false;
  try {
    await store.dispatch('agents/get');
  } catch {
    agentsError.value = true;
  } finally {
    agentsLoading.value = false;
  }
};

const loadFinanceiroResumo = async () => {
  financeiroLoading.value = true;
  financeiroError.value = false;
  try {
    const { data } = await FinanceiroAPI.resumo();
    planBadge.value = planStatusLabel(data?.plano_atual);
  } catch {
    financeiroError.value = true;
  } finally {
    financeiroLoading.value = false;
  }
};

const loadPartnerOperationsResumo = async () => {
  partnersLoading.value = true;
  partnersError.value = false;
  try {
    const { data } = await PartnersAPI.listOrganizations({
      status: 'active',
      per_page: 1,
    });
    partnersBadge.value = data?.meta?.total_count ?? 0;
  } catch {
    partnersError.value = true;
  } finally {
    partnersLoading.value = false;
  }
};

const loadQualityGovernanceResumo = async () => {
  qualityLoading.value = true;
  qualityError.value = false;
  try {
    const { data } = await DataQualityGovernanceAPI.get({ limit: 1 });
    const summary = data?.data?.summary || {};
    const openIssues = Number(summary.issues?.open_count || 0);
    const duplicateReview = Number(
      summary.duplicate_candidates?.review_required_count || 0
    );
    const fraudReview = Number(
      summary.fraud_signals?.review_required_count || 0
    );
    const total = openIssues + duplicateReview + fraudReview;
    qualityReviewCount.value = total;
    qualityBadge.value = total ? `${total} revisoes` : 'Em dia';
  } catch {
    qualityError.value = true;
  } finally {
    qualityLoading.value = false;
  }
};

const loadAgencyResumo = async () => {
  agencyLoading.value = true;
  agencyError.value = false;
  try {
    const { data } = await AgencyProfileAPI.get();
    const impact = data?.impact_summary || {};
    const blocked = Number(impact.blocked_sensitive_actions_count || 0);
    agencyBlockedCount.value = blocked;
    agencyBadge.value = blocked
      ? `${blocked} bloqueio`
      : `${impact.active_branches_count || 0} filiais`;
  } catch {
    agencyError.value = true;
  } finally {
    agencyLoading.value = false;
  }
};

const loadAdminHub = async ({ force = false } = {}) => {
  if (!isAdmin.value) return;
  if (hasLoadedAdminHub.value && !force) return;

  hasLoadedAdminHub.value = true;
  adminSummaryStartedAt.value = new Date().toISOString();
  adminSummaryFinishedAt.value = null;

  await Promise.all([
    agentManagementEnabled.value ? loadAgents() : Promise.resolve(),
    loadFinanceiroResumo(),
    loadPartnerOperationsResumo(),
    loadQualityGovernanceResumo(),
    loadAgencyResumo(),
  ]);

  adminSummaryFinishedAt.value = new Date().toISOString();

  track('admin_hub_viewed', {
    pending_invites_count: agentsError.value ? 0 : pendingInvitesCount.value,
    agent_management_enabled: agentManagementEnabled.value,
    active_partner_operations_count: partnersError.value
      ? 0
      : partnersBadge.value,
    agency_blocked_sensitive_actions_count: agencyError.value
      ? 0
      : agencyBlockedCount.value,
    data_quality_governance_review_count: qualityError.value
      ? 0
      : qualityReviewCount.value,
  });
};

const retryAdminHub = () => loadAdminHub({ force: true });

watch(
  () => route.query.tab,
  tab => {
    const normalizedTab = normalizeAdminTab(tab);
    activeTabKey.value = normalizedTab;

    if (firstQueryValue(tab) !== normalizedTab) {
      replaceRouteTab(normalizedTab);
    }
  },
  { immediate: true }
);

watch(
  isAdmin,
  allowed => {
    if (allowed) loadAdminHub();
  },
  { immediate: true }
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
  <section class="flex h-full flex-col overflow-hidden bg-[#fcfaf6]">
    <LaisHeader
      title="Administração"
      helper="Ponto de entrada único do backoffice: usuários, imobiliária, cobrança, auditoria e configurações."
    />

    <div v-if="!isAdmin" class="flex-1 overflow-auto p-4 sm:p-6 lg:p-10">
      <LaisPermissionState
        data-test="administracao-permission-state"
        variant="denied"
        title="Administração restrita"
        description="Seu perfil precisa ser administrador para carregar os resumos e atalhos deste backoffice."
        show-action
        action-label="Voltar"
        @action="router.back()"
      />
    </div>

    <div v-else class="flex-1 overflow-auto px-4 py-4 sm:px-6 lg:px-10 lg:py-6">
      <div
        v-if="isLoadingAdminHub"
        class="mb-4 flex items-center gap-2 rounded-lais border border-lais-border-soft bg-white px-4 py-3 text-[13px] font-semibold text-lais-text-muted shadow-lais-card"
        role="status"
        aria-busy="true"
        data-test="administracao-loading-state"
      >
        <span
          class="i-lucide-loader-circle size-4 animate-spin"
          aria-hidden="true"
        />
        Atualizando resumos administrativos...
      </div>

      <LaisErrorState
        v-if="hasTotalAdminSummaryError"
        data-test="administracao-error-state"
        title="Falha ao carregar Administração"
        :description="`Nao foi possivel carregar os resumos: ${failedSummaryLabels.join(', ')}.`"
        show-action
        action-label="Tentar novamente"
        :action-loading="isLoadingAdminHub"
        @action="retryAdminHub"
      />

      <template v-else>
        <div
          v-if="hasPartialAdminSummary"
          class="mb-4 rounded-lais border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800"
          role="status"
          data-test="administracao-partial-state"
        >
          <div class="flex gap-2">
            <span
              class="i-lucide-circle-alert mt-0.5 size-4 shrink-0"
              aria-hidden="true"
            />
            <div class="min-w-0">
              <p class="text-[13px] font-semibold">
                Resumo parcial da Administração
              </p>
              <p class="mt-1 text-[12px] leading-relaxed">
                Os atalhos continuam disponíveis, mas estes resumos não foram
                atualizados: {{ failedSummaryLabels.join(', ') }}.
              </p>
            </div>
          </div>
        </div>

        <LaisRunStatusPanel
          class="mb-4"
          data-test="administracao-run-status"
          v-bind="adminRunStatusPanel"
        />

        <div class="-mx-4 mb-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <div
            class="flex min-w-max gap-2 rounded-lais border border-lais-border-soft bg-white p-1 shadow-lais-card"
            role="tablist"
            aria-label="Seções canônicas da Administração"
            data-test="administracao-tabs"
          >
            <button
              v-for="tab in ADMIN_TABS"
              :id="`administracao-tab-${tab.key}-trigger`"
              :key="tab.key"
              type="button"
              role="tab"
              :aria-selected="activeTabKey === tab.key"
              :aria-controls="`administracao-tab-${tab.key}`"
              data-test="administracao-tab"
              class="flex min-h-10 shrink-0 items-center gap-2 rounded-lais px-3 py-2 text-left text-[13px] font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lais-primary"
              :class="
                activeTabKey === tab.key
                  ? 'bg-lais-primary text-white shadow-sm'
                  : 'text-lais-text-muted hover:bg-lais-bg-soft hover:text-lais-text'
              "
              @click="selectAdminTab(tab.key)"
            >
              <span>{{ tab.label }}</span>
              <span
                class="rounded-full px-2 py-0.5 text-[11px]"
                :class="
                  activeTabKey === tab.key
                    ? 'bg-white/20 text-white'
                    : 'bg-lais-bg-muted text-lais-text-muted'
                "
              >
                {{ tabCardCount(tab.key) }}
              </span>
            </button>
          </div>
        </div>

        <section
          :id="`administracao-tab-${activeTab.key}`"
          class="mb-4 rounded-lais border border-lais-border-soft bg-white px-4 py-3 shadow-lais-card"
          role="tabpanel"
          :aria-labelledby="`administracao-tab-${activeTab.key}-trigger`"
          data-test="administracao-tab-panel"
        >
          <p class="text-[14px] font-semibold text-lais-text">
            {{ activeTab.label }}
          </p>
          <p class="mt-1 text-[12px] leading-relaxed text-lais-text-muted">
            {{ activeTab.helper }}
          </p>
        </section>

        <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <LaisCard
            v-for="card in activeCards"
            :key="card.slug"
            class="transition-all"
            :class="
              card.disabled
                ? 'cursor-not-allowed opacity-60'
                : 'cursor-pointer hover:-translate-y-0.5 hover:shadow-lais-soft'
            "
            :title="
              card.disabled ? card.disabledReason || 'Em breve' : undefined
            "
            :aria-disabled="card.disabled ? 'true' : undefined"
            :role="card.disabled ? undefined : 'link'"
            :tabindex="card.disabled ? undefined : 0"
            @click="goToCard(card)"
            @keydown.enter.prevent="goToCard(card)"
            @keydown.space.prevent="goToCard(card)"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <p class="text-[15px] font-semibold text-lais-text">
                  {{ card.label }}
                </p>
                <p class="mt-1 text-[12px] leading-5 text-lais-text-muted">
                  {{ card.helper }}
                </p>
                <div class="mt-3 flex flex-wrap gap-2">
                  <span
                    v-if="card.operation"
                    class="rounded-full bg-lais-primary/10 px-2 py-0.5 text-[11px] font-semibold text-lais-primary"
                  >
                    {{ card.operation }}
                  </span>
                  <span
                    v-if="card.owner"
                    class="rounded-full bg-lais-bg-muted px-2 py-0.5 text-[11px] font-semibold text-lais-text-muted"
                  >
                    Owner: {{ card.owner }}
                  </span>
                  <span
                    v-if="card.dependency"
                    class="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800"
                  >
                    Dependência: {{ card.dependency }}
                  </span>
                </div>
              </div>
              <div class="flex shrink-0 items-center gap-2">
                <span
                  v-if="card.disabled"
                  class="rounded-full bg-lais-bg-muted px-2.5 py-0.5 text-[12px] font-semibold text-lais-text-muted"
                >
                  Em breve
                </span>
                <span
                  v-if="card.badge != null || card.badgeLoading"
                  class="rounded-full bg-lais-primary/10 px-2.5 py-0.5 text-[12px] font-semibold text-lais-primary"
                >
                  <span
                    v-if="card.badgeLoading"
                    class="block h-4 w-8 animate-pulse rounded-full bg-lais-primary/20"
                    aria-hidden="true"
                  />
                  <span v-else>{{ card.badge }}</span>
                </span>
              </div>
            </div>
            <RouterLink
              v-if="card.subTo && !card.disabled"
              :to="card.subTo"
              class="mt-3 inline-block text-[12px] font-semibold text-lais-primary hover:underline"
              @click.stop
            >
              {{ card.subLabel }}
            </RouterLink>
          </LaisCard>
        </div>
      </template>
    </div>
  </section>
</template>
